// @ts-nocheck

import RecodingMicIcon from '../../../public/images/mic-record.svg';
import { useEffect, useState, useRef } from 'react';
import {
  Filter,
  FilterRequest,
  searchProducts,
} from '../../api/searchProducts';
import { useDebouncedCallback } from 'use-debounce';
import { Product } from '../../types/product';
import { Settings, getSearchBarSettings } from '../../api/getSearchBarSettings';
import { searchConversion } from '../../api/searchConversion';
import { loggedInCoversion } from '../../api/loggedInConversion';
import { Config } from '../../types/config';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import Filters from '../FiltersComponentBuilder/filters-component';
import RIYALIcon from '../../assets/riyal.svg?react';
import {
  SESSION_ID_KEY,
  SESSION_TIMESTAMP_KEY,
  ONE_DAY_MS,
  getSessionId,
} from '../../utils/get-or-create-session';
import { safeTrack } from '../../utils/analytics';
import {
  ENTERED_FROM_SEARCH_BAR,
  CLICKED_PRODUCT_ID,
} from '../../constants/tracking';
import { setTrackingSource } from '../../utils/tracking-session';
import { handleNavigateToSearchPage } from './utils/search-page';
import { getSignedUrl } from '../../api/imageSearch';

const firebaseFunctionURL =
  'https://transcribeaudio-xez7oswrya-uc.a.run.app/transcribeAudio';

const MAX_RECORDING_TIME = 10000;

export const SEARCH_QUERY_STORAGE_KEY = 'alfinder_search_query';

export const OutsideClickHandler = ({
  children,
  onOutsideClick,
}: {
  children: React.ReactNode;
  onOutsideClick: () => void;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        wrapperRef.current &&
        event.target &&
        !wrapperRef.current.contains(target)
      ) {
        onOutsideClick();
      }
    };
    //

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onOutsideClick]);

  return <div ref={wrapperRef}>{children}</div>;
};

interface ProductGridProps {
  products: Product[];
  productsPerColumn: number;
  noOfColumns: number;
  onProductClick: (product: Product) => void;
  searchConfig: Settings;
  config?: Config;
  priceColor: string;
  currency: string;
  discountedPriceColor: string;
  showDiscounts?: boolean;
  language?: string;
}

export const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  productsPerColumn,
  noOfColumns,
  onProductClick,
  searchConfig,
  priceColor,
  config,
  currency,
  discountedPriceColor,
  showDiscounts = true,
  language = 'ar',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateMaxWidth = () => {
      if (containerRef.current) {
        const actualWidth = containerRef.current.offsetWidth;
        setContainerWidth(actualWidth);
      }
    };

    // Initial measurement
    updateMaxWidth();

    // Update on window resize
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  const getMaxTextWidth = () => {
    const imagePadding = 20;
    const imageWidth = 80;

    const columnWidth = containerWidth / noOfColumns - imagePadding;

    const maxTextWidth =
      searchConfig?.showProductImage || config?.showProductImage
        ? columnWidth - imageWidth - imagePadding
        : columnWidth - imagePadding;

    return Math.max(100, maxTextWidth);
  };

  const maxTextWidth = getMaxTextWidth();

  const getColumns = () => {
    const columns: Product[][] = Array.from({ length: noOfColumns }, () => []);
    products.forEach((product, index) => {
      const columnIndex = Math.floor(index / productsPerColumn);
      if (columnIndex < noOfColumns) {
        columns[columnIndex].push(product);
      }
    });
    return columns;
  };

  const columns = getColumns();

  const renderProductName = (name: string) => {
    return (
      <div
        style={{
          fontWeight: 700,
          maxWidth: `${maxTextWidth}px`,
        }}
        className="two-lines productName"
        title={name}
      >
        {name}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="productGrid"
      style={
        {
          '--num-columns': noOfColumns,
          display: 'grid',
        } as React.CSSProperties
      }
    >
      {products.slice(0, productsPerColumn * noOfColumns).map((product) => (
        <div
          className="productsContainer"
          key={product.id}
          onClick={() => onProductClick(product)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(searchConfig?.showProductImage || config?.showProductImage) && (
              <div
                className="imageContainer"
                style={{
                  backgroundColor: !product.mainImage ? '#D3D3D3' : 'none',
                }}
              >
                {product.mainImage && (
                  <img
                    src={product.mainImage}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    alt={
                      language === 'ar'
                        ? product.name.ar
                        : product.name.en || ''
                    }
                  />
                )}
              </div>
            )}
            <div>
              {renderProductName(
                language === 'ar' ? product.name.ar : product.name.en || '',
              )}
              <div
                className="product-price-container"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: product.discountedPrice ? '8px' : '0',
                }}
              >
                {(searchConfig?.showProductPrice ||
                  config?.showProductPrice) && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {product.discountedPrice && showDiscounts ? (
                        <>
                          <span
                            style={
                              {
                                '--price-color': priceColor,
                                display: 'flex',
                                alignItems: 'center',
                              } as React.CSSProperties
                            }
                            className="product-price"
                          >
                            {formatNumbers(
                              product.discountedPrice,
                              searchConfig?.uiStyles?.enableEnglishPrices ||
                                config?.searchStyles.enableEnglishPrices
                                ? 'en-US'
                                : 'ar-SA',
                              currency,
                            )}{' '}
                            {currency === 'SAR' && (
                              <RIYALIcon fill={priceColor} />
                            )}
                          </span>
                          <span
                            className="product-discounted-price"
                            style={{
                              textDecoration: 'line-through',
                              color: discountedPriceColor,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {formatNumbers(
                              product.price,
                              searchConfig?.uiStyles?.enableEnglishPrices ||
                                config?.searchStyles.enableEnglishPrices
                                ? 'en-US'
                                : 'ar-SA',
                              currency,
                            )}
                            {currency === 'SAR' && (
                              <RIYALIcon fill={discountedPriceColor} />
                            )}
                          </span>
                        </>
                      ) : (
                        <span
                          style={
                            {
                              '--price-color': priceColor,
                              display: 'flex',
                              alignItems: 'center',
                            } as React.CSSProperties
                          }
                          className="product-price"
                        >
                          {formatNumbers(
                            product.price,
                            searchConfig?.uiStyles?.enableEnglishPrices ||
                              config?.searchStyles.enableEnglishPrices
                              ? 'en-US'
                              : 'ar-SA',
                            currency,
                          )}
                          {currency === 'SAR' && (
                            <RIYALIcon fill={priceColor} />
                          )}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const noOfColumnsMapper = {
  'one-column': 1,
  'two-columns': 2,
  'three-columns': 3,
};

const FinderSearch = ({
  store,
  provider,
  customer,
  preview = false,
  currency = 'SAR',
  issues = false,
  searchSettings,
  language = 'ar',
  config = {
    searchStyles: {
      borderRadius: '10px',
      searchIconColor: '#ABB7C2',
      searchPlaceHolder: 'ادخل كلمة البحث',
      searchPlaceHolderEn: 'Enter search keyword',
      voiceSearchIconColor: '#FFF',
      voiceSearchBackgroundColor: '#31728F',
      enableEnglishPrices: false,
      priceColor: '#31728F',
      discountedPriceColor: '#ad1a10',
      isVoiceSearchEnabled: true,
      searchResultsTitleColor: '#000',
    },
    dynamicFiltersConfig: {
      isDynamicFiltersEnabled: true,
      allowedDynamicFilters: ['price', 'categories'],
      dynamicFiltersPrimaryColor: '#055D7F',
      dynamicFiltersCountColor: '#000',
    },
  },
}: {
  store: number;
  provider: string;
  customer: string;
  preview?: boolean;
  config?: Config;
  currency?: string;
  issues?: boolean;
  searchSettings?: Settings;
  language?: string;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [imageSearchUrl, setImageSearchUrl] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [dynamicFilters, setDynamicFilters] = useState<Filter[] | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiceSearchLoading, setVoiceSearchLoading] = useState(false);
  const [searchConfig, setSearchConfig] = useState<Settings | null>(
    searchSettings || null,
  );
  const isDynamicFiltersEnabled = preview
    ? config.dynamicFiltersConfig?.isDynamicFiltersEnabled ?? true
    : searchConfig?.uiStyles?.isDynamicFiltersEnabled ?? true;
  const storeType = provider;
  const customerId = customer === 'undefined' ? undefined : customer;
  const [recording, setRecording] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const audioStream = useRef<MediaStream | null>(null);
  const recordingTimeout = useRef<NodeJS.Timeout | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  const inputFieldBorderRadius =
    searchConfig?.uiStyles?.borderRadius || config.searchStyles.borderRadius;

  const [appliedFilters, setAppliedFilters] = useState<Record<
    string,
    string[] | { from: number; to: number }
  > | null>(null);

  const abortController = useRef<AbortController | null>(null);
  const [lastSearchTime, setLastSearchTime] = useState<number | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const enableVoiceSearch =
    (searchConfig?.uiStyles?.isVoiceSearchEnabled === true ||
      config.searchStyles.isVoiceSearchEnabled === true) &&
    !(
      searchConfig?.uiStyles?.isVoiceSearchEnabled === false ||
      config.searchStyles.isVoiceSearchEnabled === false
    );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileName = `${
    import.meta.env.VITE_ALFINDER_URL?.includes('staging')
      ? 'staging'
      : 'production'
  }/images/image_search_${store}_${new Date().getTime()}`;

  const isDaralamiratMobile =
    window.location.hostname === 'daralamirat.com.sa' &&
    window.innerWidth <= 768;

  const handleFilterChange = (
    key: string,
    value: string[] | { from: number; to: number },
  ) => {
    safeTrack('search_bar_filter_changed', {
      query: searchQuery,
      storeId: store,
      provider: storeType,
      customerId,
      type: 'search_bar',
      key: key,
    });
    setAppliedFilters((prev) => {
      const updatedFilters = { ...prev, [key]: value };

      // Remove the filter if value is empty or reset to null if no filters remain
      if (Array.isArray(value) && value.length === 0) {
        delete updatedFilters[key];
      }

      // Track as a new search when filters change
      if (searchQuery.trim()) {
        void handleSearchEvent('filter_changed');
      }

      const hasFilters = Object.keys(updatedFilters).length > 0;
      return hasFilters ? updatedFilters : null;
    });
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // If there's a pending request, cancel it
    if (abortController.current) {
      abortController.current.abort();
      setLoading(false); // Reset loading state when aborting
    }

    // If the entire text was selected and cleared at once
    if (searchQuery.length > 0 && newValue === '') {
      handleClearSearch();
      return;
    }

    if (newValue.length === 0) {
      setOpenDialog(false);
    }
    setSearchQuery(newValue);
  };

  const transformFilters = (
    appliedFilters: Record<string, string[] | { from: number; to: number }>,
  ): FilterRequest => {
    return {
      filters: Object.entries(appliedFilters)
        .map(([key, value]) => {
          if (
            key === 'price' &&
            typeof value === 'object' &&
            'from' in value &&
            'to' in value
          ) {
            // Handle price filter
            return {
              key,
              from: value.from.toString(),
              to: value.to.toString(),
            };
          } else if (Array.isArray(value) && value.length > 0) {
            // Handle other filters
            return {
              key,
              values: value,
            };
          }
          return null; // Exclude filters with empty arrays or invalid formats
        })
        .filter(Boolean) as FilterRequest['filters'], // Filter out null values
    };
  };

  const search = async (
    query: string,
    filters?: Record<string, string[] | { from: number; to: number }>,
  ) => {
    // Create new AbortController for this request
    abortController.current = new AbortController();

    let transformedFilters;
    transformedFilters = filters ? transformFilters(filters) : undefined;

    try {
      const response = await searchProducts({
        storeId: store,
        language,
        storeType,
        ...(!imageSearchUrl ? { query } : { image: query }),
        filters: transformedFilters,
        currency,
        limit: searchConfig?.productsPerColumn
          ? searchConfig?.productsPerColumn *
            noOfColumnsMapper[
              searchConfig?.resultsNumberOfColumns || 'one-column'
            ]
          : config.productsPerColumn *
            noOfColumnsMapper[config.resultsNumberOfColumns || 'one-column'],
        signal: abortController.current.signal, // Add abort signal
      });
      return {
        products: response.results.products,
        filters: response.results.filters,
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        // Handle abort error silently
        return {
          products: [],
          filters: [],
        };
      }
      throw error;
    }
  };

  const fetchWithFilters = async () => {
    const { products } = await search(
      !imageSearchUrl ? searchQuery : imageSearchUrl,
      appliedFilters,
    );
    setSearchResults(products);
  };

  const handleLoggedInConversion = async (sessionId: string) => {
    return await loggedInCoversion({
      sessionId,
      storeType,
      storeId: store,
      customerId,
    });
  };

  // const getSettings = async () => {
  //   const settings = await getSearchBarSettings(store, storeType);
  //   setSearchConfig(settings);
  // };

  const handleSearch = useDebouncedCallback(async () => {
    if (!voiceSearchLoading) {
      setLoading(true);
    }
    const { products, filters } = await search(searchQuery);
    // if (!preview) {
    //   safeTrack('search_performed', {
    //     query: searchQuery,
    //     storeId: store,
    //     provider: storeType,
    //     customerId: customerId,
    //     type: 'after_search_api_call',
    //   });
    // }
    setSearchResults(products);
    setDynamicFilters(filters);
    if (!openDialog) {
      setOpenDialog(true);
    }

    if (!voiceSearchLoading) {
      setLoading(false);
    } else {
      setVoiceSearchLoading(false);
    }
  }, 1000);

  const stopRecording = () => {
    if (recordingTimeout.current) {
      clearTimeout(recordingTimeout.current); // Clear the timeout if the button is released early
      recordingTimeout.current = null;
    }

    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
      audioStream.current?.getTracks().forEach((track) => track.stop());
      audioStream.current = null;
    }
    setRecording(false);
  };

  const handleVoiceRecognition = async (
    event: React.MouseEvent | React.TouchEvent,
  ) => {
    // Prevent default touch behavior
    if (event.type === 'touchstart') {
      event.preventDefault();
    }

    if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        audioStream.current = stream; // Save the audio stream to stop it later
        mediaRecorder.current = new MediaRecorder(stream);

        mediaRecorder.current.ondataavailable = (event: BlobEvent) => {
          audioChunks.current.push(event.data);
        };

        mediaRecorder.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks.current, {
            type: 'audio/webm',
          });
          audioChunks.current = [];

          // Stop all tracks in the stream to release the microphone
          audioStream.current?.getTracks().forEach((track) => track.stop());
          audioStream.current = null;

          // Convert Blob to Base64
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = async () => {
            const base64Audio = reader.result as string;

            // Send base64-encoded audio to Firebase function
            setVoiceSearchLoading(true);
            const response = await fetch(firebaseFunctionURL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ audio: base64Audio }),
            });
            try {
              const result = await response.json();
              if (result.transcription.length) {
                setSearchQuery(result.transcription);
                safeTrack('voice_search_performed', {
                  query: result.transcription,
                  storeId: store,
                  provider: storeType,
                  customerId,
                  type: 'search_bar',
                });
              } else {
                setVoiceSearchLoading(false);
              }
            } catch (e) {
              setVoiceSearchLoading(false);
            }
          };
        };

        mediaRecorder.current.start();
        setRecording(true);

        recordingTimeout.current = setTimeout(() => {
          stopRecording();
        }, MAX_RECORDING_TIME);
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    }
  };

  const handleClearSearch = async () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
    setLoading(false); // Reset loading state when clearing
    setSearchQuery('');
    setImageSearchUrl(''); // Clear image URL as well
    setSearchResults([]);
    setAppliedFilters(null);
    setDynamicFilters(null);
    setOpenDialog(false);
    // handleSearchEvent('query_cleared');
  };

  const handleOutsideClick = () => {
    setOpenDialog(false);
    handleSearchEvent('modal_closed');
  };

  const handleProductClick = async (product: Product) => {
    if (!preview) {
      setTrackingSource(ENTERED_FROM_SEARCH_BAR, product.productId.toString());
      await handleSearchEvent('product_clicked');
      safeTrack('product_clicked', {
        query: searchQuery,
        storeId: store,
        provider: storeType,
        customerId: customerId,
        product: product.productId,
        type: 'search_bar',
        productName: product.name.ar,
      });
      window.open(
        language === 'ar' ? product.urlTrans?.ar : product.urlTrans?.en,
        '_self',
      );
    }
  };

  const handleSearchEvent = async (type: string) => {
    if (preview || !searchQuery.trim()) {
      return;
    }

    const previousQueries = localStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
    const searchedQueries = previousQueries ? JSON.parse(previousQueries) : [];
    const trimmedQuery = searchQuery.trim();
    const isNewQuery = !searchedQueries.includes(trimmedQuery);

    // Always track events for 'enter' and 'product_clicked'
    if (type === 'enter' || type === 'product_clicked') {
      safeTrack('search_performed', {
        query: trimmedQuery,
        storeId: store,
        provider: storeType,
        customerId,
        type,
      });

      if (isNewQuery) {
        searchedQueries.push(trimmedQuery);
        localStorage.setItem(
          SEARCH_QUERY_STORAGE_KEY,
          JSON.stringify(searchedQueries),
        );
      }
    }
    // For other event types, only track if it's a new query and store it
    else if (isNewQuery) {
      safeTrack('search_performed', {
        query: trimmedQuery,
        storeId: store,
        provider: storeType,
        customerId,
        type,
      });

      searchedQueries.push(trimmedQuery);
      localStorage.setItem(
        SEARCH_QUERY_STORAGE_KEY,
        JSON.stringify(searchedQueries),
      );
    }
  };

  // Handle scroll event in modal
  useEffect(() => {
    if (openDialog && searchResults.length > 0) {
      const modalElement = document.querySelector('.search-results-container');
      if (modalElement) {
        const handleScroll = () => {
          void handleSearchEvent('scroll');
          // Remove listener after first scroll
          modalElement.removeEventListener('scroll', handleScroll);
        };
        modalElement.addEventListener('scroll', handleScroll);
        return () => modalElement.removeEventListener('scroll', handleScroll);
      }
    }
  }, [openDialog, searchResults]);

  // Handle 7 second timeout
  useEffect(() => {
    if (searchQuery.trim().length >= 3) {
      setLastSearchTime(Date.now());

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        void handleSearchEvent('timeout');
      }, 30000);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      (searchQuery.trim() || imageSearchUrl.length) &&
      !preview
    ) {
      e.preventDefault();
      void handleSearchEvent('enter');
      handleNavigateToSearchPage(
        storeType,
        searchQuery,
        searchConfig?.isSearchPageEnabled,
        imageSearchUrl,
      );
    }
  };

  useEffect(() => {
    if (!preview) {
      // void getSettings();
      const sessionId = getSessionId();

      if (customerId && sessionId) {
        handleLoggedInConversion(sessionId);
      }
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length >= 3) {
      setImageSearchUrl('');
      setAppliedFilters(null);
      setDynamicFilters(null);
      void handleSearch();
    } else {
      setImageSearchUrl('');
      setSearchResults([]);
      setDynamicFilters(null);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.trim().length || imageSearchUrl.length) {
      void fetchWithFilters();
    }
  }, [appliedFilters]);

  const isPriceOnlyFilterWithZeroRange = (filters: Filter[] | null) => {
    if (!filters || filters.length !== 1) return false;
    const filter = filters[0];
    return filter.key === 'price' && filter.min === '0' && filter.max === '0';
  };

  const handleCameraIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear previous selection
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    console.log('File upload triggered'); // Debug log
    setLoading(true);
    const file = event.target.files?.[0];
    console.log({ file });
    if (file) {
      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxFileSize) {
        console.error('File size exceeds 5MB limit');
        setLoading(false);
        return;
      }

      try {
        console.log('Getting upload URL'); // Debug log
        const url = await getSignedUrl(store, fileName, 'image_search');
        console.log('Upload URL received:', url); // Debug log
        await fetch(url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'image/jpeg',
          },
        });
        const imageUrl = `https://storage.alfinder.ai/image_search/${fileName}`;
        setImageSearchUrl(imageUrl);
        console.log('File uploaded to storage'); // Debug log
        const response = await searchProducts({
          storeId: store,
          storeType: provider,
          image: imageUrl,
          currency,
          language,
          limit: searchConfig?.productsPerColumn
            ? searchConfig?.productsPerColumn *
              noOfColumnsMapper[
                searchConfig?.resultsNumberOfColumns || 'one-column'
              ]
            : config.productsPerColumn *
              noOfColumnsMapper[config.resultsNumberOfColumns || 'one-column'],
        });
        setSearchResults(response.results.products);
        setDynamicFilters(response.results.filters);
        if (!openDialog) {
          setOpenDialog(true);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setLoading(false);
      }
    } else {
      console.log('No file selected'); // Debug log
      setLoading(false);
    }
  };

  return (
    <div className="alfinder-search">
      <OutsideClickHandler onOutsideClick={handleOutsideClick}>
        <div
          style={{
            position: 'relative',
          }}
        >
          <div className="alfinder-searchContainer">
            <div className="alfinder-inputWrapper">
              <svg
                className="alfinder-searchIcon"
                style={{
                  zIndex: 11111111,
                }}
                width="15"
                height="15"
                viewBox="0 0 23 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.7859 20.7039L16.7703 15.5182C17.9778 13.8562 18.6297 11.8332 18.6275 9.75446C18.6275 4.44481 14.4493 0.125 9.31374 0.125C4.17818 0.125 0 4.44481 0 9.75446C0 15.0641 4.17818 19.3839 9.31374 19.3839C11.3243 19.3862 13.281 18.7122 14.8885 17.4638L19.9042 22.6494C20.1581 22.8841 20.4892 23.0093 20.8296 22.9995C21.17 22.9896 21.4939 22.8454 21.7347 22.5964C21.9755 22.3475 22.115 22.0127 22.1245 21.6607C22.134 21.3088 22.0129 20.9664 21.7859 20.7039ZM2.66107 9.75446C2.66107 8.39408 3.05124 7.06426 3.78225 5.93315C4.51325 4.80203 5.55226 3.92044 6.76787 3.39985C7.98349 2.87925 9.32112 2.74304 10.6116 3.00844C11.9021 3.27383 13.0875 3.92892 14.0179 4.89085C14.9483 5.85278 15.5819 7.07836 15.8386 8.41259C16.0953 9.74683 15.9635 11.1298 15.46 12.3866C14.9565 13.6435 14.1038 14.7177 13.0098 15.4735C11.9157 16.2292 10.6295 16.6326 9.31374 16.6326C7.54999 16.6305 5.85909 15.9051 4.61193 14.6157C3.36477 13.3262 2.66318 11.578 2.66107 9.75446Z"
                  fill={
                    searchConfig?.uiStyles?.searchIconColor ||
                    config.searchStyles.searchIconColor
                  }
                />
              </svg>

              <input
                className="alfinder-searchInput"
                placeholder={
                  imageSearchUrl
                    ? '' // No placeholder when image is present
                    : language === 'ar'
                    ? searchConfig?.uiStyles?.searchPlaceHolder ||
                      config.searchStyles.searchPlaceHolder
                    : searchConfig?.uiStyles?.searchPlaceHolderEn ||
                      config.searchStyles.searchPlaceHolderEn
                }
                style={
                  {
                    '--search-input-height': isDaralamiratMobile
                      ? '46px'
                      : '50px',
                    borderRadius: inputFieldBorderRadius,
                    '::placeholder': { color: '#c6c6c6' },
                    paddingInlineStart: imageSearchUrl ? '75px' : undefined, // Add padding for image
                  } as React.CSSProperties
                }
                onClick={() => {
                  if (
                    (searchResults && searchResults.length) ||
                    (dynamicFilters &&
                      !isPriceOnlyFilterWithZeroRange(dynamicFilters))
                  ) {
                    if (!openDialog) {
                      setOpenDialog(true);
                    }
                  }
                }}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                value={searchQuery}
              />

              {/* Image preview when imageSearchUrl is present */}
              {imageSearchUrl && (
                <div
                  style={{
                    position: 'absolute',
                    insetInlineStart: '40px', // Position after search icon
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 11111112,
                    pointerEvents: 'none',
                  }}
                >
                  <img
                    src={imageSearchUrl}
                    alt="Search image"
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '4px',
                      objectFit: 'cover',
                      border: '1px solid #e0e0e0',
                    }}
                  />
                </div>
              )}

              {(Boolean(searchQuery.length) || Boolean(imageSearchUrl)) &&
                !loading && (
                  <div
                    className="alfinder-closeIcon"
                    onClick={handleClearSearch}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 50 50"
                      width="15px"
                      style={{
                        width: '15px',
                      }}
                    >
                      <path d="M 9.15625 6.3125 L 6.3125 9.15625 L 22.15625 25 L 6.21875 40.96875 L 9.03125 43.78125 L 25 27.84375 L 40.9375 43.78125 L 43.78125 40.9375 L 27.84375 25 L 43.6875 9.15625 L 40.84375 6.3125 L 25 22.15625 Z" />
                    </svg>
                  </div>
                )}

              {!loading &&
                !searchQuery.length &&
                !imageSearchUrl.length &&
                enableVoiceSearch && (
                  <div
                    className={`${
                      recording
                        ? 'recording'
                        : voiceSearchLoading
                        ? 'mic-loading'
                        : 'micIcon'
                    }`}
                    style={
                      {
                        '--voice-search-solid-bg': !recording
                          ? searchConfig?.uiStyles
                              ?.voiceSearchBackgroundColor ||
                            config.searchStyles.voiceSearchBackgroundColor
                          : undefined,
                        zIndex: 11111111,
                      } as React.CSSProperties
                    }
                    onMouseDown={handleVoiceRecognition}
                    onMouseUp={stopRecording}
                    onMouseLeave={stopRecording}
                    onTouchStart={handleVoiceRecognition}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      stopRecording();
                    }}
                    onTouchCancel={(e) => {
                      e.preventDefault();
                      stopRecording();
                    }}
                  >
                    {!recording ? (
                      <svg
                        fill={
                          !voiceSearchLoading
                            ? searchConfig?.uiStyles?.voiceSearchIconColor ||
                              config.searchStyles.voiceSearchIconColor
                            : searchConfig?.uiStyles
                                ?.voiceSearchBackgroundColor ||
                              config.searchStyles.voiceSearchBackgroundColor
                        }
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        enable-background="new 0 0 512 512"
                        stroke={
                          searchConfig?.uiStyles?.voiceSearchIconColor ||
                          config.searchStyles.voiceSearchIconColor
                        }
                        style={{ width: '20px' }}
                      >
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />

                        <g
                          id="SVGRepo_tracerCarrier"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />

                        <g id="SVGRepo_iconCarrier">
                          <g>
                            <g>
                              <path d="m439.5,236c0-11.3-9.1-20.4-20.4-20.4s-20.4,9.1-20.4,20.4c0,70-64,126.9-142.7,126.9-78.7,0-142.7-56.9-142.7-126.9 0-11.3-9.1-20.4-20.4-20.4s-20.4,9.1-20.4,20.4c0,86.2 71.5,157.4 163.1,166.7v57.5h-23.6c-11.3,0-20.4,9.1-20.4,20.4 0,11.3 9.1,20.4 20.4,20.4h88c11.3,0 20.4-9.1 20.4-20.4 0-11.3-9.1-20.4-20.4-20.4h-23.6v-57.5c91.6-9.3 163.1-80.5 163.1-166.7z" />
                              <path d="m256,323.5c51,0 92.3-41.3 92.3-92.3v-127.9c0-51-41.3-92.3-92.3-92.3s-92.3,41.3-92.3,92.3v127.9c0,51 41.3,92.3 92.3,92.3zm-52.3-220.2c0-28.8 23.5-52.3 52.3-52.3s52.3,23.5 52.3,52.3v127.9c0,28.8-23.5,52.3-52.3,52.3s-52.3-23.5-52.3-52.3v-127.9z" />
                            </g>
                          </g>
                        </g>
                      </svg>
                    ) : (
                      <img src={RecodingMicIcon} style={{ width: '20px' }} />
                    )}
                  </div>
                )}

              {!loading &&
                !searchQuery.length &&
                !imageSearchUrl.length &&
                searchConfig?.isImageSearchEnabled && (
                  <div
                    className="cameraIcon"
                    style={
                      {
                        zIndex: 11111111,
                        insetInlineEnd: !enableVoiceSearch ? '10px' : '50px',
                      } as React.CSSProperties
                    }
                    onClick={handleCameraIconClick}
                  >
                    <input
                      type="file"
                      accept="image/jpeg, image/png, image/jpg"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                    />
                    <svg
                      width="30px"
                      height="30px"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        {' '}
                        <circle
                          cx="12"
                          cy="13"
                          r="3"
                          stroke="#000000"
                          stroke-width="1.5"
                        ></circle>{' '}
                        <path
                          d="M2 13.3636C2 10.2994 2 8.76721 2.74902 7.6666C3.07328 7.19014 3.48995 6.78104 3.97524 6.46268C4.69555 5.99013 5.59733 5.82123 6.978 5.76086C7.63685 5.76086 8.20412 5.27068 8.33333 4.63636C8.52715 3.68489 9.37805 3 10.3663 3H13.6337C14.6219 3 15.4728 3.68489 15.6667 4.63636C15.7959 5.27068 16.3631 5.76086 17.022 5.76086C18.4027 5.82123 19.3044 5.99013 20.0248 6.46268C20.51 6.78104 20.9267 7.19014 21.251 7.6666C22 8.76721 22 10.2994 22 13.3636C22 16.4279 22 17.9601 21.251 19.0607C20.9267 19.5371 20.51 19.9462 20.0248 20.2646C18.9038 21 17.3433 21 14.2222 21H9.77778C6.65675 21 5.09624 21 3.97524 20.2646C3.48995 19.9462 3.07328 19.5371 2.74902 19.0607C2.53746 18.7498 2.38566 18.4045 2.27673 18"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        ></path>{' '}
                        <path
                          d="M19 10H18"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        ></path>{' '}
                      </g>
                    </svg>
                  </div>
                )}

              {loading && (
                <span
                  className="loader-alfinder"
                  style={{
                    border: `3px solid ${
                      searchConfig?.uiStyles?.voiceSearchBackgroundColor ||
                      config.searchStyles.voiceSearchBackgroundColor
                    }`,
                    borderBottomColor: 'transparent',
                  }}
                ></span>
              )}
            </div>
          </div>

          {openDialog && (!loading || !voiceSearchLoading) && (
            <div
              className="elfinder-modal"
              style={{
                width: issues ? '500px' : '100%',
              }}
            >
              {(Boolean(searchResults && searchResults?.length) ||
                (dynamicFilters &&
                  !isPriceOnlyFilterWithZeroRange(dynamicFilters))) &&
                isDynamicFiltersEnabled && (
                  <div
                    style={{
                      width: issues ? '500px' : '100%',
                      padding: '10px 10px 0 10px',
                      overflowX: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#30728f #eaf0f3',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#eaf0f3',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#30728f',
                        borderRadius: '4px',
                      },
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '40px',
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ marginInlineEnd: '10px' }}
                        >
                          <g opacity="0.5">
                            <path
                              d="M21 4H14"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M10 4H3"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M21 12H12"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M8 12H3"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M21 20H16"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M12 20H3"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M14 2V6"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M8 10V14"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M16 18V22"
                              stroke={
                                searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              }
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </g>
                        </svg>
                        <div
                          className="dynamic-filters-title"
                          style={{
                            // fontSize: '16px',
                            color:
                              searchConfig?.uiStyles
                                ?.dynamicFiltersPrimaryColor ||
                              config.dynamicFiltersConfig
                                ?.dynamicFiltersPrimaryColor,
                            fontWeight: 700,
                          }}
                        >
                          {language === 'ar'
                            ? 'تصفية النتائج'
                            : 'Filter Results'}
                        </div>
                      </div>
                      {appliedFilters && (
                        <button
                          onClick={() => setAppliedFilters(null)}
                          style={{
                            color: showFilters
                              ? searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor
                              : 'white',
                            backgroundColor: showFilters
                              ? 'white'
                              : searchConfig?.uiStyles
                                  ?.dynamicFiltersPrimaryColor ||
                                config.dynamicFiltersConfig
                                  ?.dynamicFiltersPrimaryColor,
                            borderRadius: '10px',
                            width: '150px',
                            height: '40px',
                            textAlign: 'center',
                            border: showFilters
                              ? `1px solid ${
                                  searchConfig?.uiStyles
                                    ?.dynamicFiltersPrimaryColor ||
                                  config.dynamicFiltersConfig
                                    ?.dynamicFiltersPrimaryColor
                                }`
                              : 'none',
                            fontSize: 'inherit',
                          }}
                        >
                          {language === 'ar'
                            ? 'إعادة تعيين الفلاتر'
                            : 'Reset Filters'}
                        </button>
                      )}
                    </div>
                    {dynamicFilters && !loading ? (
                      <div>
                        {showFilters && (
                          <Filters
                            currency={currency}
                            availableFilters={
                              searchConfig?.uiStyles?.allowedDynamicFilters ||
                              config.dynamicFiltersConfig?.allowedDynamicFilters
                            }
                            primaryColor={
                              searchConfig?.uiStyles
                                ?.dynamicFiltersPrimaryColor ||
                              config.dynamicFiltersConfig
                                ?.dynamicFiltersPrimaryColor
                            }
                            countColor={
                              searchConfig?.uiStyles
                                ?.dynamicFiltersCountColor ||
                              config.dynamicFiltersConfig
                                ?.dynamicFiltersCountColor
                            }
                            filters={dynamicFilters}
                            appliedFilters={appliedFilters || {}}
                            onFilterChange={handleFilterChange}
                            language={
                              searchConfig?.uiStyles?.enableEnglishPrices ||
                              config?.searchStyles.enableEnglishPrices
                                ? 'en-US'
                                : 'ar-SA'
                            }
                            locale={language}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="alfinder-filters-loading-container">
                        <div className="alfinder-filters-loading"></div>
                        <div className="alfinder-filters-loading"></div>
                        <div className="alfinder-filters-loading"></div>
                        <div className="alfinder-filters-loading"></div>
                      </div>
                    )}
                  </div>
                )}
              <div
                style={{
                  height:
                    Boolean(searchResults && searchResults.length) ||
                    (Boolean(dynamicFilters) &&
                      !isPriceOnlyFilterWithZeroRange(dynamicFilters))
                      ? '320px'
                      : '100px',
                  overflowY: 'auto',
                }}
                className="search-results-container"
              >
                <div
                  style={{
                    width: '100%',
                    overflowY: 'auto',
                  }}
                >
                  {Boolean(searchResults && searchResults.length) ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '10px',
                          marginInlineStart: '10px',
                          marginTop:
                            searchConfig?.uiStyles?.isDynamicFiltersEnabled ||
                            config.dynamicFiltersConfig?.isDynamicFiltersEnabled
                              ? '0px'
                              : '10px',
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 23 23"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            marginInlineEnd: '10px',
                          }}
                        >
                          <path
                            d="M21.7859 20.7039L16.7703 15.5182C17.9778 13.8562 18.6297 11.8332 18.6275 9.75446C18.6275 4.44481 14.4493 0.125 9.31374 0.125C4.17818 0.125 0 4.44481 0 9.75446C0 15.0641 4.17818 19.3839 9.31374 19.3839C11.3243 19.3862 13.281 18.7122 14.8885 17.4638L19.9042 22.6494C20.1581 22.8841 20.4892 23.0093 20.8296 22.9995C21.17 22.9896 21.4939 22.8454 21.7347 22.5964C21.9755 22.3475 22.115 22.0127 22.1245 21.6607C22.134 21.3088 22.0129 20.9664 21.7859 20.7039ZM2.66107 9.75446C2.66107 8.39408 3.05124 7.06426 3.78225 5.93315C4.51325 4.80203 5.55226 3.92044 6.76787 3.39985C7.98349 2.87925 9.32112 2.74304 10.6116 3.00844C11.9021 3.27383 13.0875 3.92892 14.0179 4.89085C14.9483 5.85278 15.5819 7.07836 15.8386 8.41259C16.0953 9.74683 15.9635 11.1298 15.46 12.3866C14.9565 13.6435 14.1038 14.7177 13.0098 15.4735C11.9157 16.2292 10.6295 16.6326 9.31374 16.6326C7.54999 16.6305 5.85909 15.9051 4.61193 14.6157C3.36477 13.3262 2.66318 11.578 2.66107 9.75446Z"
                            fill={
                              searchConfig?.uiStyles?.searchResultsTitleColor ||
                              config.searchStyles.searchResultsTitleColor
                            }
                          />
                        </svg>
                        <div
                          className="search-results-title"
                          style={{
                            // fontSize: '16px',
                            color:
                              searchConfig?.uiStyles?.searchResultsTitleColor ||
                              config.searchStyles.searchResultsTitleColor,
                          }}
                        >
                          {language === 'ar'
                            ? `نتائج البحث (${searchResults.length} منتج)`
                            : `Search Results (${searchResults.length} products)`}
                        </div>
                      </div>
                      <ProductGrid
                        products={searchResults as Product[]}
                        productsPerColumn={
                          searchConfig?.productsPerColumn ||
                          config.productsPerColumn ||
                          1
                        }
                        noOfColumns={
                          noOfColumnsMapper[
                            searchConfig?.resultsNumberOfColumns ||
                              config.resultsNumberOfColumns
                          ]
                        }
                        onProductClick={handleProductClick}
                        searchConfig={searchConfig as Settings}
                        config={config}
                        priceColor={
                          searchConfig?.uiStyles?.priceColor ||
                          config.searchStyles.priceColor
                        }
                        discountedPriceColor={
                          searchConfig?.uiStyles?.discountedPriceColor ||
                          config.searchStyles.discountedPriceColor
                        }
                        showDiscounts={searchConfig?.showDiscounts}
                        currency={currency}
                        language={language}
                      />
                    </>
                  ) : (
                    <div
                      style={{
                        padding: '30px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      {searchConfig?.noResultsText || config.noResultsText}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default FinderSearch;
